import React, { useState, useEffect, useMemo } from 'react';
import { signInWithPopup, sendSignInLinkToEmail } from 'firebase/auth';
import { auth, googleProvider, isFirebaseConfigured } from '../src/firebaseConfig';
import { XIcon } from './icons/XIcon';
import { SparkleIcon } from './icons/SparkleIcon';
import { ProfessionalSession } from '../types';
import { GlobeIcon } from './icons/GlobeIcon';
import { SpinnerIcon } from './icons/SpinnerIcon';
import { ShieldIcon } from './icons/ShieldIcon';
import { ExternalLinkIcon } from './icons/ExternalLinkIcon';
import { ClipboardListIcon } from './icons/ClipboardListIcon';

interface AccountCreationModalProps {
    onClose: () => void;
    onCreateAccountSuccess: (user: any) => void;
    tempSession: ProfessionalSession | null;
}

const AccountCreationModal: React.FC<AccountCreationModalProps> = ({ onClose, onCreateAccountSuccess, tempSession }) => {
    const [email, setEmail] = useState('');
    const [error, setError] = useState<React.ReactNode | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [linkSent, setLinkSent] = useState(false);
    const [copied, setCopied] = useState(false);

    const currentHostname = useMemo(() => {
        try {
            return window.location.hostname || 'localhost';
        } catch (e) {
            return 'localhost';
        }
    }, []);

    useEffect(() => {
        if (tempSession && tempSession.activeClientId) {
            const clientEmail = tempSession.clients[tempSession.activeClientId]?.userData.email;
            if (clientEmail) setEmail(clientEmail);
        }
    }, [tempSession]);

    const handleGoogleSignIn = async () => {
        if (!isFirebaseConfigured || !auth) {
            setError("Firebase is not configured. Please add your API keys.");
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const result = await signInWithPopup(auth, googleProvider);
            onCreateAccountSuccess(result.user);
        } catch (err: any) {
            console.error("Google Sign-In Error:", err);
            setError(err.message || "Google sign-in failed.");
        } finally {
            setIsLoading(false);
        }
    };

    const copyDomain = () => {
        navigator.clipboard.writeText(currentHostname);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleMagicLinkSignIn = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) {
            setError("Please enter your email.");
            return;
        }

        setIsLoading(true);
        setError(null);

        const redirectUrl = window.location.origin + '/?email_link=true';

        const actionCodeSettings = {
            url: redirectUrl,
            handleCodeInApp: true,
            // iOS/Android integration could be added here if needed for native apps
        };

        try {
            if (isFirebaseConfigured && auth) {
                console.log("[AUTH] Sending magic link. Redirect:", redirectUrl);
                await sendSignInLinkToEmail(auth, email, actionCodeSettings);
                window.localStorage.setItem('emailForSignIn', email);
                setLinkSent(true);
            } else {
                setError(
                    <div className="text-left bg-amber-50 border-2 border-amber-200 p-5 rounded-2xl animate-fade-in shadow-inner">
                        <div className="flex items-center gap-2 mb-3 text-amber-700">
                            <ShieldIcon className="w-5 h-5" />
                            <p className="font-black uppercase text-xs tracking-widest">Firebase Not Ready</p>
                        </div>
                        <p className="text-[11px] text-amber-900 mb-3 font-bold leading-tight">
                            Your Firebase API keys are missing or invalid. Magic links cannot be sent until configuration is complete.
                        </p>
                        <p className="text-[10px] text-amber-800 font-medium italic">
                            Check your environment variables (VITE_FIREBASE_API_KEY, etc.) and ensure they are correctly set.
                        </p>
                    </div>
                );
            }
        } catch (err: any) {
            console.error("Magic Link Error:", err);
            if (err.code === 'auth/operation-not-allowed') {
                setError(
                    <div className="text-left bg-red-50 border-2 border-red-200 p-5 rounded-2xl animate-fade-in shadow-inner">
                        <div className="flex items-center gap-2 mb-3 text-red-700">
                            <ShieldIcon className="w-5 h-5" />
                            <p className="font-black uppercase text-xs tracking-widest">Firebase Config Missing</p>
                        </div>
                        <p className="text-[11px] text-red-900 mb-3 font-bold leading-tight">
                            Magic Link (Passwordless) login must be toggled ON in your Firebase project console.
                        </p>
                        <ol className="text-[10px] list-decimal list-inside space-y-2 text-red-800 font-bold mb-4">
                            <li>Open <strong>Authentication &gt; Sign-in method</strong> in Firebase.</li>
                            <li>Select <strong>Email/Password</strong>.</li>
                            <li>Enable <strong>"Email link (passwordless sign-in)"</strong>.</li>
                            <li>Click <strong>Save</strong>.</li>
                        </ol>
                        <a 
                            href="https://console.firebase.google.com/project/forgivepath-8a2ed/authentication/providers" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-full bg-red-600 hover:bg-red-700 text-white text-[10px] font-black py-3 rounded-xl transition-all flex items-center justify-center gap-2 uppercase tracking-widest shadow-lg shadow-red-200"
                        >
                            Open Firebase for forgivepath-8a2ed
                            <ExternalLinkIcon className="w-3.5 h-3.5" />
                        </a>
                    </div>
                );
            } else if (err.code === 'auth/unauthorized-continue-uri') {
                setError(
                    <div className="text-left bg-orange-50 border-2 border-orange-200 p-5 rounded-2xl animate-fade-in shadow-inner">
                        <div className="flex items-center gap-2 mb-3 text-orange-700">
                            <ShieldIcon className="w-5 h-5" />
                            <p className="font-black uppercase text-xs tracking-widest">Domain Not Allowed</p>
                        </div>
                        <p className="text-[11px] text-orange-900 mb-4 font-bold leading-tight">
                            Your host <strong>{currentHostname}</strong> is not authorized. If this is localhost, ensure it is in your Firebase settings.
                        </p>
                        <div className="space-y-4">
                            <div className="p-3 bg-white rounded-xl border border-orange-200">
                                <p className="text-[9px] text-orange-400 uppercase font-black mb-1">Copy this exact string:</p>
                                <div className="flex items-center justify-between gap-2">
                                    <code className="text-xs font-black text-orange-900 break-all">{currentHostname}</code>
                                    <button 
                                        onClick={copyDomain}
                                        className="flex-shrink-0 bg-orange-600 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-tighter hover:bg-orange-700"
                                    >
                                        {copied ? 'Copied!' : 'Copy'}
                                    </button>
                                </div>
                            </div>
                            <ol className="text-[10px] list-decimal list-inside space-y-2 text-orange-800 font-bold mb-5">
                                <li>Open <strong>Firebase Settings</strong> below.</li>
                                <li>Find <strong>Authorized domains</strong>.</li>
                                <li>Add the domain you just copied and Save.</li>
                            </ol>
                            <a 
                                href="https://console.firebase.google.com/project/forgivepath-8a2ed/authentication/settings" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-full bg-orange-600 hover:bg-orange-700 text-white text-[10px] font-black py-3 rounded-xl transition-all flex items-center justify-center gap-2 uppercase tracking-widest shadow-lg shadow-orange-200"
                            >
                                Fix Domain in Firebase
                                <ExternalLinkIcon className="w-3.5 h-3.5" />
                            </a>
                        </div>
                    </div>
                );
            } else {
                setError(<p className="text-red-600 font-bold text-xs">Failed: {err.message || "Could not send link."}</p>);
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (linkSent) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-10 text-center relative animate-fade-in">
                    <button onClick={onClose} className="absolute top-6 right-6 text-gray-400 hover:text-gray-700"><XIcon className="w-6 h-6" /></button>
                    <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                        <SparkleIcon className="w-10 h-10 animate-pulse" />
                    </div>
                    <h2 className="text-3xl font-black text-gray-900 mb-4 uppercase tracking-tighter">Check Your Email</h2>
                    <p className="text-gray-600 mb-8 leading-relaxed">
                        We sent a magic sign-in link to <strong className="text-blue-600">{email}</strong>. 
                        Click the link in your inbox to securely access your account.
                    </p>
                    <button onClick={onClose} className="w-full bg-gray-100 text-gray-600 font-bold py-4 rounded-2xl hover:bg-gray-200 transition-all">
                        Got it, I'll check my mail
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden relative animate-fade-in">
                <div className="p-10">
                    <button onClick={onClose} className="absolute top-6 right-6 text-gray-400 hover:text-gray-700"><XIcon className="w-6 h-6" /></button>
                    
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-2xl mb-4 rotate-3">
                            <SparkleIcon className="w-8 h-8 text-blue-600" />
                        </div>
                        <h2 className="text-3xl font-black text-gray-900 uppercase tracking-tighter">Welcome to ForgivePath</h2>
                        <p className="text-gray-500 mt-2 font-medium">No passwords. Just simple, secure access.</p>
                    </div>

                    <div className="space-y-6">
                        <form onSubmit={handleMagicLinkSignIn} className="space-y-4">
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Secure Email Access</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full p-4 bg-gray-50 border-2 border-gray-100 rounded-2xl focus:border-blue-500 focus:bg-white focus:outline-none transition-all text-sm font-bold"
                                    placeholder="Enter your email address"
                                    required
                                    disabled={isLoading}
                                />
                            </div>

                            {error && (
                                <div className="mt-2">
                                    {error}
                                </div>
                            )}

                            <button 
                                type="submit" 
                                disabled={isLoading || !email}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-4 rounded-2xl shadow-lg shadow-blue-500/20 transition-all flex items-center justify-center gap-2 disabled:bg-gray-300"
                            >
                                {isLoading ? <SpinnerIcon className="w-5 h-5 animate-spin" /> : "Email me a Sign-In Link"}
                            </button>
                        </form>

                        <div className="relative flex items-center py-2">
                            <div className="flex-grow border-t border-gray-100"></div>
                            <span className="flex-shrink mx-4 text-gray-400 text-[10px] font-black uppercase tracking-widest">or continue with</span>
                            <div className="flex-grow border-t border-gray-100"></div>
                        </div>

                        <button 
                            onClick={handleGoogleSignIn}
                            disabled={isLoading}
                            className="w-full flex items-center justify-center gap-3 bg-gray-50 border-2 border-gray-100 hover:bg-white hover:border-gray-300 text-gray-500 font-bold py-3 rounded-2xl transition-all disabled:opacity-50 text-xs"
                        >
                            <GlobeIcon className="w-5 h-5 grayscale opacity-70" />
                            Sign in with Google
                        </button>
                    </div>
                    
                    <p className="text-[10px] text-gray-400 mt-8 text-center font-bold uppercase tracking-widest leading-relaxed">
                        By signing in, you agree to our <br/>
                        <span className="text-blue-600">Terms of Service</span> & <span className="text-blue-600">Privacy Policy</span>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AccountCreationModal;